<template>
  <div 
    v-if="visible"
    :class="{...cssClasses}"
  >
    <fr-label 
      for-id="fr-state"
      :class="{'hidden': field.meta.hideLabel}"
    >
      {{ label || field.meta.label }}
    </fr-label>
    <fr-select
      id="fr-state"
      ref="selectField"
      v-model="fieldValue"
      :valid="!fieldHasErrors('fr-state')"
      :required="field.admin.required"
      name="fr-state"
      :options="stateOptions"
    />
  </div>
</template>

<script>
import defaultStates from '@/data/defaults/states.js'
import FrLabel from '@/components/fields/base/FrLabel.vue'
import FrSelect from '@/components/fields/base/FrSelect.vue'
import helpers from '@/helpers/helpers'

export default {
  name: 'FrState',

  components: {
    FrLabel,
    FrSelect
  },

  inject: ['store'],

  props: {
    /**
     * The form field that is being rendered.
     */
    field: {
      type: Object,
      required: true
    },

    /** 
     * The value that is passed from the parent component through `v-model`.
     */
    modelValue: {
      type: [Array, Object, String, Number, Boolean],
      default: undefined
    }
  },

  emits: ['update:modelValue'],

  computed: {
    /**
     * The getters mapped from Pinia.
     */
    formFields() { return this.store.getFormFields },
    submitObject() { return this.store.getSubmitObject },
    programsFilter() { return this.store.getProgramsFilter },
    selectedCountry() { return this.store.getSelectedCountry },

    /** 
     * The CSS classes to be applied to the element.
     * 
     * This is defined as a computed property so we can dynamically set classes.
     * 
     * @returns {Array}
     */
    cssClasses() {
      return {
        'fr-state': true
      }
    },
    
    /**
     * The value that is passed from the parent component through `v-model`.
     * 
     * This is wrapped as a computed property so that it may be bound 
     * as a `v-model` to a child component. Setting this up as a proxy 
     * bypasses the `Avoid mutating a prop directly` error thrown by Vue.
     * Instead, we intercept this mutation and pass it along to the parent.
     * 
     * @param {Object} val
     * 
     * @returns {Object}
     */
    fieldValue: {
      get() { return this.modelValue },
      set(val) { this.$emit('update:modelValue', val) }
    },

    /**
     * The default options to be rendered by the programs filter.
     * 
     * @returns {Array}
     */
    defaultOptions() {
      return [{
        label: this.field.meta.placeholder ?? 'Please select a State',
        value: '',
        selected: true,
        disabled: true
      }]
    },

    /**
     * The states to display on the form.
     * 
     * States may be overridden to have values either replaced, or appended.
     * 
     * @returns {Array}
     */
    stateOptions() {
      let states = []

      // We want to set the default US states if we're selecting 'United States of America' and we're not replacing the list.
      if (this.selectedCountry === 'United States of America' && this.field.meta.optionsMode !== 'replace') {
        states.push(...defaultStates)
      }

      if (this.field.meta.options.length > 0) {
        states.push(...this.field.meta.options)
      }

      states = this.sanitizeStateOptions(states)

      let filteredStates = states.filter(state => state.condition === this.selectedCountry || ! state.condition)

      return [
        ...this.defaultOptions,
        ...filteredStates.sort((a, b) => a.label.localeCompare(b.label)) // Sort alphabetically by label.
      ]
    },

    /**
     * For display of field
     * @returns {Boolean}
     */
    visible() {
      return this.stateOptions.length > 1
    },
    
    /**
     * For dynamic label settings
     * @returns {String}
     */
    label() {
      //if no attribute, return null for the default form config val to display
       if(!this.field.meta.display) {
        return null
      }

      if(this.field.meta.display.length === 0) {
        return null
      }
      //if length, if display name is not null or an empty string, else default
      if(this.field.meta.display.length > 0 && this.field.meta.display[0].displayName && this.field.meta.display[0].displayName !== '')  {
        //if conditional logic evaluates to true
        if(helpers.conditionalLogicEvaluation('display', this.formFields, this.field.meta, this.submitObject, this.programsFilter)) {
          return this.field.meta.display[0].displayName
        }
        return null      
      }

      return null

    }   

  },

  watch: {
    /**
     * Sets the validation to ignore if the field is not visible
     * @param {*} newVal 
     */
    visible(newVal) { 
      let steps = this.store.setIgnoreValidation(this.field.id, !newVal)
      const { schema, labelLut } = this.store.generateStepsSchema(steps)
      this.store.setSchemaObjectValue({key: "schema", value: schema})
      if(!newVal){
        this.fieldValue = this.field.meta.default ?? ''
      }
    }
  },


  created() {
    let steps = this.store.setIgnoreValidation(this.field.id, !this.visible)
    const { schema, labelLut } = this.store.generateStepsSchema(steps)
    this.store.setSchemaObjectValue({key: "schema", value: schema})  
  },

  methods: {

    /**
     * Sanitizes the state options to include the `selected` and `disabled` keys.
     * 
     * In the event of default countries, the condition will be set to `''`.
     * 
     * @param {Array}
     */
    sanitizeStateOptions(options) {
      let sanitizedOptions = []
      let optionMap = {
        selected: false,
        disabled: false,
        condition: null
      }

      options.forEach(option => {
        for (let [key, value] of Object.entries(optionMap)) {
          if (! Object.prototype.hasOwnProperty.call(option, key)) {
            option[key] = value
          }
        }

        sanitizedOptions.push(option)
      })

      return sanitizedOptions
    },

    fieldHasErrors(name) {
      return this.store.fieldHasErrors(name)
    },

    focus() {
      this.$refs.selectField.focus();
    }
  }
}
</script>

<style lang="scss" scoped>
// Styles go hurr.
</style>
